import { useState, useCallback, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Network,
  Shield,
  Users,
  Loader2,
  Plus,
  MoreHorizontal,
  FileDown,
  Copy,
  CheckCircle,
  XCircle,
  Trash2,
  Eye,
  Globe,
  RefreshCw,
  Activity,
  AlertTriangle,
  Building2,
  User,
  RotateCcw,
  Database,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { CustomerTunnelsCard } from "@/components/portal/CustomerTunnelsCard";
import { ManagedDatabasesTab } from "@/components/portal/platform/ManagedDatabasesTab";
import { IpsecConnectionsTab } from "@/components/portal/platform/IpsecConnectionsTab";
import { WireguardFirewallRequirements } from "@/components/portal/platform/WireguardFirewallRequirements";
import { isActiveIpsecConnection } from "@/components/portal/platform/ipsecConnection";
import { fetchWireguardHealth, type WireguardHealth } from "@/lib/portalData";
import { isSuperAdmin } from "@/lib/roles";
import { usePermissions } from "@/hooks/usePermissions";
import {
  platformApi,
  VpnPeer,
  VpnPeerType,
  VpnStatusResponse,
  VpnPeerConfigResponse,
  DynDnsDomain,
  DynDnsUpdateLog,
  IpsecConnectionsListResponse,
} from "@/services/api/platform";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";
import { QRCodeSVG } from "qrcode.react";

export const Route = createFileRoute("/portal/platform/infrastructure")({
  component: InfrastructurePage,
});

function InfrastructurePage() {
  const { token, session } = useAuth();
  const { organisations } = usePortalData();
  const { can } = usePermissions();

  // Admin pages across /portal/platform/* show platform-wide data whenever the
  // user is a platform admin — the org selector is independent of admin scope.
  const isAdminView = isSuperAdmin(session);

  // WI3: VPN peers are billable (R250/connection) and their config carries the
  // client private key. org_member is read-only on vpn at the gateway, so gate
  // the mutating/credential actions in the UI too (BE=FE parity). Create,
  // revoke, revive and config-export all require write access to vpn.
  const canManageVpn = can("create", "vpn");

  const orgNameById = new Map(
    organisations.map((o) => [o.organisationId, o.organisationName || o.slug || "—"]),
  );

  // VPN state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [peerTypeFilter, setPeerTypeFilter] = useState<VpnPeerType | "all">("all");
  const [statusFilter, setStatusFilter] = useState<"active" | "revoked" | "all">("active");
  const [vpnStatus, setVpnStatus] = useState<VpnStatusResponse | null>(null);
  const [vpnHealth, setVpnHealth] = useState<WireguardHealth | null>(null);

  // VPN dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [selectedPeer, setSelectedPeer] = useState<VpnPeer | null>(null);
  const [peerConfig, setPeerConfig] = useState<VpnPeerConfigResponse | null>(null);

  // VPN create form state
  const [createName, setCreateName] = useState("");
  const [createTunnelMode, setCreateTunnelMode] = useState<"full" | "split">("split");
  const [createPeerType, setCreatePeerType] = useState<VpnPeerType>("internal");
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  // DynDNS state
  const [dnsSearchTerm, setDnsSearchTerm] = useState("");
  const [dnsSortBy, setDnsSortBy] = useState("created_at_utc");
  const [dnsSortOrder, setDnsSortOrder] = useState<"asc" | "desc">("desc");
  const [showDnsCreateDialog, setShowDnsCreateDialog] = useState(false);
  const [showDnsDeleteDialog, setShowDnsDeleteDialog] = useState(false);
  const [showDnsLogsDialog, setShowDnsLogsDialog] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<DynDnsDomain | null>(null);
  const [domainLogs, setDomainLogs] = useState<DynDnsUpdateLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // DynDNS create form state
  const [createDomainName, setCreateDomainName] = useState("");
  const [createSubdomain, setCreateSubdomain] = useState("");
  const [createRecordType, setCreateRecordType] = useState("A");
  const [createTtl, setCreateTtl] = useState("300");
  const [creatingDomain, setCreatingDomain] = useState(false);
  const [deletingDomain, setDeletingDomain] = useState(false);

  // DynDNS API key dialog state
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [apiKeyData, setApiKeyData] = useState<{ api_key: string; hostname: string } | null>(null);
  const [regeneratingKey, setRegeneratingKey] = useState(false);

  // --- VPN data fetch ---
  const fetchPeers = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<VpnPeer>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };

      const response = await platformApi.listVpnPeers(token, {
        page,
        per_page: pageSize,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        peer_type: peerTypeFilter !== "all" ? peerTypeFilter : undefined,
        status: statusFilter,
      });

      try {
        const status = await platformApi.getVpnStatus(token);
        setVpnStatus(status);
      } catch {
        setVpnStatus(null);
      }

      // Customer-tunnel health is a SEPARATE authority (the Control Plane
      // collector) from the raw interface dump above, and must fail
      // independently: if the dump is unreachable the health card should still
      // render, and vice versa. Null on failure, never a zeroed object — a
      // fabricated 0 offline would read as "all client sites up".
      try {
        setVpnHealth(await fetchWireguardHealth(token));
      } catch {
        setVpnHealth(null);
      }

      return {
        data: response.peers || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, searchTerm, sortBy, sortOrder, peerTypeFilter, statusFilter],
  );

  const {
    data: peers,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<VpnPeer>({
    fetchFn: fetchPeers,
    initialPageSize: 25,
    dependencies: [token, searchTerm, sortBy, sortOrder, peerTypeFilter],
  });

  // --- IPsec data fetch (platform admins only: the wireguard API 403s
  // everyone else, so a non-admin never requests it) ---
  const [ipsec, setIpsec] = useState<IpsecConnectionsListResponse | null>(null);
  const [ipsecLoading, setIpsecLoading] = useState(false);
  const [ipsecError, setIpsecError] = useState<string | null>(null);

  const loadIpsec = useCallback(async () => {
    if (!token || !isAdminView) return;
    setIpsecLoading(true);
    try {
      setIpsec(await platformApi.listIpsecConnections(token));
      setIpsecError(null);
    } catch (err) {
      setIpsecError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIpsecLoading(false);
    }
  }, [token, isAdminView]);

  useEffect(() => {
    void loadIpsec();
  }, [loadIpsec]);

  // --- DynDNS data fetch ---
  const fetchDomains = useCallback(
    async (page: number, pageSize: number): Promise<PaginatedResponse<DynDnsDomain>> => {
      if (!token) return { data: [], totalCount: 0, page, pageSize };

      const response = await platformApi.listDynDnsDomains(token, {
        page,
        page_size: pageSize,
        search: dnsSearchTerm || undefined,
        sort_by: dnsSortBy,
        sort_order: dnsSortOrder,
      });

      return {
        data: response.items || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, dnsSearchTerm, dnsSortBy, dnsSortOrder],
  );

  const {
    data: domains,
    isLoading: dnsLoading,
    error: dnsError,
    pagination: dnsPagination,
    pageSizeOptions: dnsPageSizeOptions,
    goToPage: dnsGoToPage,
    nextPage: dnsNextPage,
    prevPage: dnsPrevPage,
    firstPage: dnsFirstPage,
    lastPage: dnsLastPage,
    setPageSize: dnsSetPageSize,
    refresh: dnsRefresh,
    canGoNext: dnsCanGoNext,
    canGoPrev: dnsCanGoPrev,
    startIndex: dnsStartIndex,
    endIndex: dnsEndIndex,
  } = usePaginatedData<DynDnsDomain>({
    fetchFn: fetchDomains,
    initialPageSize: 25,
    dependencies: [token, dnsSearchTerm, dnsSortBy, dnsSortOrder],
  });

  // --- VPN sorting ---
  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }

  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  // --- DynDNS sorting ---
  function handleDnsSort(column: string) {
    if (dnsSortBy === column) {
      setDnsSortOrder(dnsSortOrder === "asc" ? "desc" : "asc");
    } else {
      setDnsSortBy(column);
      setDnsSortOrder("asc");
    }
  }

  function getDnsSortDirection(column: string): "asc" | "desc" | null {
    return dnsSortBy === column ? dnsSortOrder : null;
  }

  // --- VPN handlers ---
  const handleCreatePeer = async () => {
    if (!token || !createName.trim()) return;
    setCreating(true);
    try {
      const newPeer = await platformApi.createVpnPeer(token, {
        name: createName.trim(),
        tunnel_mode: createTunnelMode,
        peer_type: createPeerType,
      });
      toast.success(`Peer "${newPeer.name}" created successfully`);
      setShowCreateDialog(false);
      setCreateName("");
      setCreateTunnelMode("split");
      setCreatePeerType("internal");

      setSelectedPeer(newPeer);
      await loadConfigForPeer(newPeer.id);
      refresh();
    } catch (err) {
      console.error("Failed to create peer:", err);
      toast.error(
        `Failed to create peer: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setCreating(false);
    }
  };

  const loadConfigForPeer = async (peerId: string) => {
    if (!token) return;
    setConfigLoading(true);
    try {
      const config = await platformApi.getVpnPeerConfig(token, peerId);
      setPeerConfig(config);
      setShowConfigDialog(true);
    } catch (err) {
      console.error("Failed to load config:", err);
      toast.error(
        `Failed to load peer config: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setConfigLoading(false);
    }
  };

  const handleRevokePeer = async () => {
    if (!token || !selectedPeer) return;
    setRevoking(true);
    try {
      await platformApi.revokeVpnPeer(token, selectedPeer.id);
      toast.success(`Peer "${selectedPeer.name}" revoked`);
      setShowRevokeDialog(false);
      setSelectedPeer(null);
      refresh();
    } catch (err) {
      console.error("Failed to revoke peer:", err);
      toast.error(
        `Failed to revoke peer: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setRevoking(false);
    }
  };

  const handleRevivePeer = async (peer: VpnPeer) => {
    if (!token) return;
    try {
      await platformApi.reviveVpnPeer(token, peer.id);
      toast.success(`Peer "${peer.name}" revived`);
      refresh();
    } catch (err) {
      console.error("Failed to revive peer:", err);
      toast.error(
        `Failed to revive peer: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    }
  };

  const handleDownloadConfig = () => {
    if (!peerConfig) return;
    const blob = new Blob([peerConfig.config], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = peerConfig.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopyConfig = async () => {
    if (!peerConfig) return;
    try {
      await navigator.clipboard.writeText(peerConfig.config);
      toast.success("Config copied to clipboard");
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  // --- DynDNS handlers ---
  const handleCreateDomain = async () => {
    if (!token || !createDomainName.trim() || !createSubdomain.trim()) return;
    setCreatingDomain(true);
    try {
      const newDomain = await platformApi.createDynDnsDomain(token, {
        domain_name: createDomainName.trim(),
        subdomain: createSubdomain.trim(),
        record_type: createRecordType,
        ttl: parseInt(createTtl, 10),
      });
      toast.success(`Domain "${newDomain.hostname}" created successfully`);
      setShowDnsCreateDialog(false);
      setCreateDomainName("");
      setCreateSubdomain("");
      setCreateRecordType("A");
      setCreateTtl("300");

      setApiKeyData({ api_key: newDomain.api_key, hostname: newDomain.hostname });
      setShowApiKeyDialog(true);

      dnsRefresh();
    } catch (err) {
      console.error("Failed to create domain:", err);
      toast.error(
        `Failed to create domain: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setCreatingDomain(false);
    }
  };

  const handleRegenerateApiKey = async (domain: DynDnsDomain) => {
    if (!token) return;
    setRegeneratingKey(true);
    try {
      const result = await platformApi.regenerateDynDnsApiKey(token, domain.id);
      setApiKeyData({ api_key: result.api_key, hostname: result.hostname });
      setShowApiKeyDialog(true);
      toast.success("API key regenerated successfully");
    } catch (err) {
      console.error("Failed to regenerate API key:", err);
      toast.error(
        `Failed to regenerate API key: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setRegeneratingKey(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!apiKeyData) return;
    try {
      await navigator.clipboard.writeText(apiKeyData.api_key);
      toast.success("API key copied to clipboard");
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleDeleteDomain = async () => {
    if (!token || !selectedDomain) return;
    setDeletingDomain(true);
    try {
      await platformApi.deleteDynDnsDomain(token, selectedDomain.id);
      toast.success(`Domain "${selectedDomain.hostname}" deleted`);
      setShowDnsDeleteDialog(false);
      setSelectedDomain(null);
      dnsRefresh();
    } catch (err) {
      console.error("Failed to delete domain:", err);
      toast.error(
        `Failed to delete domain: ${err instanceof Error ? err.message : "Unknown error"}`,
      );
    } finally {
      setDeletingDomain(false);
    }
  };

  const loadDomainLogs = async (domain: DynDnsDomain) => {
    if (!token) return;
    setSelectedDomain(domain);
    setLogsLoading(true);
    setShowDnsLogsDialog(true);
    try {
      const response = await platformApi.getDynDnsDomainLogs(token, domain.id, {
        page: 1,
        page_size: 50,
      });
      setDomainLogs(response.items);
    } catch (err) {
      console.error("Failed to load logs:", err);
      toast.error("Failed to load update logs");
      setDomainLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  // --- Helpers ---
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <Badge variant="success">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        );
      case "revoked":
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Revoked
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPeerTypeBadge = (peerType: VpnPeerType) => {
    switch (peerType) {
      case "internal":
        return (
          <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">
            <User className="mr-1 h-3 w-3" />
            Internal
          </Badge>
        );
      case "customer":
        return (
          <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
            <Building2 className="mr-1 h-3 w-3" />
            Customer
          </Badge>
        );
      default:
        return <Badge variant="outline">{peerType}</Badge>;
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  // API serialises MSSQL DateTime columns as naive ISO strings (no 'Z'),
  // so JS would parse them as local time. Treat them as UTC explicitly.
  const parseUtc = (dateStr: string): Date => {
    const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr);
    return new Date(hasTz ? dateStr : `${dateStr}Z`);
  };

  const formatHandshake = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    const date = parseUtc(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.floor(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return formatDate(dateStr);
  };

  const formatLiveHandshake = (unixSeconds: number | null) => {
    if (!unixSeconds) return "Never";
    return formatHandshake(new Date(unixSeconds * 1000).toISOString());
  };

  const peerLastSeen = (peer: VpnPeer) => {
    if (peer.latest_handshake && peer.latest_handshake > 0) {
      return formatLiveHandshake(peer.latest_handshake);
    }
    return formatHandshake(peer.last_handshake_at);
  };

  /**
   * Freshness of the live tunnel, from the most recent handshake we know
   * about (live `latest_handshake` from `wg show dump`, else the persisted
   * `last_handshake_at` written by the poller). Returned in milliseconds.
   */
  const peerHandshakeAgeMs = (peer: VpnPeer): number | null => {
    const candidates: number[] = [];
    if (peer.latest_handshake && peer.latest_handshake > 0) {
      candidates.push(peer.latest_handshake * 1000);
    }
    if (peer.last_handshake_at) {
      const t = parseUtc(peer.last_handshake_at).getTime();
      if (!Number.isNaN(t)) candidates.push(t);
    }
    if (candidates.length === 0) return null;
    const newest = Math.max(...candidates);
    return Date.now() - newest;
  };

  /**
   * Health badge based on handshake age:
   *   <2m    → Healthy (green)
   *   2–10m  → Stale (amber)
   *   >10m   → Offline (red)
   *   never  → Offline (slate)
   * Revoked peers bypass this and show the normal status badge.
   */
  const getPeerHealthBadge = (peer: VpnPeer) => {
    if (peer.status === "revoked") return getStatusBadge(peer.status);

    const ageMs = peerHandshakeAgeMs(peer);
    if (ageMs === null) {
      return (
        <Badge variant="outline" className="text-slate-600 border-slate-200 bg-slate-50 w-fit">
          <XCircle className="mr-1 h-3 w-3" />
          Offline
        </Badge>
      );
    }
    if (ageMs < 2 * 60 * 1000) {
      return (
        <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50 w-fit">
          <Activity className="mr-1 h-3 w-3" />
          Healthy
        </Badge>
      );
    }
    if (ageMs < 10 * 60 * 1000) {
      return (
        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 w-fit">
          <AlertTriangle className="mr-1 h-3 w-3" />
          Stale
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-red-700 border-red-200 bg-red-50 w-fit">
        <XCircle className="mr-1 h-3 w-3" />
        Offline
      </Badge>
    );
  };

  const formatBytes = (bytes: number | null) => {
    if (bytes === null || bytes === undefined) return "—";
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KiB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MiB`;
    return `${(mb / 1024).toFixed(2)} GiB`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Infrastructure</h1>
          <p className="mt-2 text-lg text-slate-600">
            VPN and Dynamic DNS management
          </p>
        </div>
      </div>

      <Tabs defaultValue="vpn" className="space-y-6">
        <TabsList>
          <TabsTrigger value="vpn" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            WireGuard ({pagination.totalCount})
          </TabsTrigger>
          {isAdminView && (
            <TabsTrigger value="ipsec" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              IPsec ({ipsec?.connections?.filter(isActiveIpsecConnection).length ?? 0})
            </TabsTrigger>
          )}
          <TabsTrigger value="dyndns" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            DynDNS ({dnsPagination.totalCount})
          </TabsTrigger>
          {isAdminView && (
            <TabsTrigger value="managed-databases" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Managed databases
            </TabsTrigger>
          )}
        </TabsList>

        {/* ================================================================ */}
        {/* VPN Tab                                                          */}
        {/* ================================================================ */}
        <TabsContent value="vpn" className="space-y-6">
          {error ? (
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="text-center py-12 text-muted-foreground">
                <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500 opacity-50" />
                <p className="text-lg font-medium mb-2">Failed to load VPN data</p>
                <p className="text-sm mb-4">{error}</p>
                <Button onClick={refresh} variant="outline">
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Status Cards */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">VPN Status</CardTitle>
                    <Shield
                      className={`h-4 w-4 ${vpnStatus ? "text-green-500" : "text-muted-foreground"}`}
                    />
                  </CardHeader>
                  <CardContent>
                    <div
                      className={`text-2xl font-bold ${vpnStatus ? "text-green-600" : "text-muted-foreground"}`}
                    >
                      {vpnStatus ? "Connected" : "Unknown"}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {vpnStatus
                        ? `Interface: ${vpnStatus.interface}`
                        : "Status unavailable"}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Peers</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {vpnStatus?.total_peers ?? pagination.totalCount}
                    </div>
                    <p className="text-xs text-muted-foreground">Registered peers</p>
                  </CardContent>
                </Card>
                <CustomerTunnelsCard health={vpnHealth} />
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Peer Types</CardTitle>
                    <Network className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {peers.filter((p) => p.peer_type === "internal").length} / {peers.filter((p) => p.peer_type === "customer").length}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Internal / Customer on this page
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Peers Panel */}
              <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
                {/* Controls bar */}
                <div className="flex flex-wrap items-center gap-3">
                  <SearchInput
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClear={() => setSearchTerm("")}
                    placeholder="Search peers..."
                    containerClassName="flex-1 min-w-[200px]"
                  />
                  <Select
                    value={peerTypeFilter}
                    onValueChange={(v) => setPeerTypeFilter(v as VpnPeerType | "all")}
                  >
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="internal">Internal</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={statusFilter}
                    onValueChange={(v) => setStatusFilter(v as "active" | "revoked" | "all")}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="revoked">Revoked</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={refresh}>
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Refresh
                  </Button>
                  {canManageVpn && (
                    <Button size="sm" onClick={() => setShowCreateDialog(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add Peer
                    </Button>
                  )}
                </div>

                {/* Table */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : peers.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      {searchTerm || peerTypeFilter !== "all"
                        ? "No peers match your filters"
                        : "No VPN peers yet"}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {searchTerm || peerTypeFilter !== "all"
                        ? "Try a different search term or filter."
                        : "Create your first VPN peer to get started."}
                    </p>
                    {!searchTerm && peerTypeFilter === "all" && canManageVpn && (
                      <Button onClick={() => setShowCreateDialog(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Peer
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead
                            direction={getSortDirection("name")}
                            onSort={() => handleSort("name")}
                          >
                            Peer
                          </SortableTableHead>
                          {isAdminView && (
                            <SortableTableHead direction={null} onSort={() => {}} disabled>
                              Organisation
                            </SortableTableHead>
                          )}
                          <SortableTableHead
                            direction={getSortDirection("peer_type")}
                            onSort={() => handleSort("peer_type")}
                          >
                            Type
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("allowed_ips")}
                            onSort={() => handleSort("allowed_ips")}
                          >
                            IP Address
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("tunnel_mode")}
                            onSort={() => handleSort("tunnel_mode")}
                          >
                            Tunnel
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("status")}
                            onSort={() => handleSort("status")}
                          >
                            Status
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("last_handshake_at")}
                            onSort={() => handleSort("last_handshake_at")}
                          >
                            Last Seen
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getSortDirection("created_at")}
                            onSort={() => handleSort("created_at")}
                          >
                            Created
                          </SortableTableHead>
                          <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">
                            {" "}
                          </SortableTableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {peers.map((peer) => (
                          <TableRow
                            key={peer.id}
                            className={peer.status === "revoked" ? "opacity-60" : undefined}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className={`h-10 w-10 rounded-md flex items-center justify-center ${
                                  peer.peer_type === "customer"
                                    ? "bg-amber-50"
                                    : "bg-primary/10"
                                }`}>
                                  {peer.peer_type === "customer" ? (
                                    <Building2 className="h-5 w-5 text-amber-600" />
                                  ) : (
                                    <User className="h-5 w-5 text-primary" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium">{peer.name}</div>
                                  <div className="text-xs text-muted-foreground font-mono">
                                    {peer.public_key.substring(0, 20)}...
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            {isAdminView && (
                              <TableCell>
                                <span className="text-sm">
                                  {orgNameById.get(peer.organisation_id) || (
                                    <span className="text-muted-foreground">
                                      {peer.organisation_id.substring(0, 8)}…
                                    </span>
                                  )}
                                </span>
                              </TableCell>
                            )}
                            <TableCell>{getPeerTypeBadge(peer.peer_type)}</TableCell>
                            <TableCell>
                              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                {peer.allowed_ips}
                              </code>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs capitalize">
                                {peer.tunnel_mode}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                {getPeerHealthBadge(peer)}
                                {!peer.in_wg && peer.status !== "revoked" && (
                                  <span className="text-[10px] text-amber-600">Not on interface</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                <span className="text-sm text-muted-foreground">
                                  {peerLastSeen(peer)}
                                </span>
                                {(peer.transfer_rx !== null || peer.transfer_tx !== null) && (
                                  <span className="text-[10px] text-muted-foreground font-mono">
                                    ↓ {formatBytes(peer.transfer_rx)} · ↑ {formatBytes(peer.transfer_tx)}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {formatDate(peer.created_at)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {/* WI3: the peer actions (View Config exposes the
                                  client private key; Revive/Revoke are billable)
                                  are hidden for read-only members. */}
                              {canManageVpn && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedPeer(peer);
                                        loadConfigForPeer(peer.id);
                                      }}
                                    >
                                      <Eye className="h-4 w-4 mr-2" />
                                      View Config
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {peer.status === "revoked" ? (
                                      <DropdownMenuItem onClick={() => handleRevivePeer(peer)}>
                                        <RotateCcw className="h-4 w-4 mr-2" />
                                        Revive
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={() => {
                                          setSelectedPeer(peer);
                                          setShowRevokeDialog(true);
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Revoke
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {pagination.totalCount > 0 && (
                      <div className="mt-4 border-t pt-4">
                        <Pagination
                          page={pagination.page}
                          totalPages={pagination.totalPages}
                          totalCount={pagination.totalCount}
                          pageSize={pagination.pageSize}
                          pageSizeOptions={pageSizeOptions}
                          startIndex={startIndex}
                          endIndex={endIndex}
                          canGoPrev={canGoPrev}
                          canGoNext={canGoNext}
                          onPageChange={goToPage}
                          onPageSizeChange={setPageSize}
                          onFirstPage={firstPage}
                          onLastPage={lastPage}
                          onPrevPage={prevPage}
                          onNextPage={nextPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* ================================================================ */}
        {/* IPsec Tab (platform admin, read-only: created by the CLI)        */}
        {/* ================================================================ */}
        {isAdminView && (
          <TabsContent value="ipsec" className="space-y-6">
            <IpsecConnectionsTab
              data={ipsec}
              isLoading={ipsecLoading}
              error={ipsecError}
              onRefresh={() => void loadIpsec()}
              orgNameById={orgNameById}
            />
          </TabsContent>
        )}

        {/* ================================================================ */}
        {/* DynDNS Tab                                                       */}
        {/* ================================================================ */}
        <TabsContent value="dyndns" className="space-y-6">
          {dnsError ? (
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <div className="text-center py-12 text-muted-foreground">
                <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500 opacity-50" />
                <p className="text-lg font-medium mb-2">Failed to load DynDNS data</p>
                <p className="text-sm mb-4">{dnsError}</p>
                <Button onClick={dnsRefresh} variant="outline">
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Status Cards */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Domains</CardTitle>
                    <Globe className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dnsPagination.totalCount}</div>
                    <p className="text-xs text-muted-foreground">Registered domains</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Domains</CardTitle>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {domains.filter((d) => d.is_active).length}
                    </div>
                    <p className="text-xs text-muted-foreground">Currently active</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Updates</CardTitle>
                    <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {domains.reduce((sum, d) => sum + d.update_count, 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">IP updates recorded</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Record Types</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {domains.filter((d) => d.record_type === "A").length} A / {domains.filter((d) => d.record_type === "AAAA").length} AAAA
                    </div>
                    <p className="text-xs text-muted-foreground">IPv4 / IPv6</p>
                  </CardContent>
                </Card>
              </div>

              {/* Domains Panel */}
              <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
                {/* Controls bar */}
                <div className="flex flex-wrap items-center gap-3">
                  <SearchInput
                    value={dnsSearchTerm}
                    onChange={(e) => setDnsSearchTerm(e.target.value)}
                    onClear={() => setDnsSearchTerm("")}
                    placeholder="Search domains..."
                    containerClassName="flex-1 min-w-[200px]"
                  />
                  <Button variant="outline" size="sm" onClick={dnsRefresh}>
                    {dnsLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Refresh
                  </Button>
                  <Button size="sm" onClick={() => setShowDnsCreateDialog(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Domain
                  </Button>
                </div>

                {/* Table */}
                {dnsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : domains.length === 0 ? (
                  <div className="text-center py-12">
                    <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      {dnsSearchTerm ? "No domains match your search" : "No domains yet"}
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      {dnsSearchTerm
                        ? "Try a different search term."
                        : "Create your first domain to get started."}
                    </p>
                    {!dnsSearchTerm && (
                      <Button onClick={() => setShowDnsCreateDialog(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Domain
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead
                            direction={getDnsSortDirection("hostname")}
                            onSort={() => handleDnsSort("hostname")}
                          >
                            Hostname
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getDnsSortDirection("current_ip")}
                            onSort={() => handleDnsSort("current_ip")}
                          >
                            Current IP
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getDnsSortDirection("record_type")}
                            onSort={() => handleDnsSort("record_type")}
                          >
                            Type
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getDnsSortDirection("ttl")}
                            onSort={() => handleDnsSort("ttl")}
                          >
                            TTL
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getDnsSortDirection("is_active")}
                            onSort={() => handleDnsSort("is_active")}
                          >
                            Status
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getDnsSortDirection("update_count")}
                            onSort={() => handleDnsSort("update_count")}
                          >
                            Updates
                          </SortableTableHead>
                          <SortableTableHead
                            direction={getDnsSortDirection("last_update_at_utc")}
                            onSort={() => handleDnsSort("last_update_at_utc")}
                          >
                            Last Update
                          </SortableTableHead>
                          <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">
                            {" "}
                          </SortableTableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {domains.map((domain) => (
                          <TableRow key={domain.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                                  <Globe className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                  <div className="font-medium">{domain.hostname}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {domain.subdomain}.{domain.domain_name}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {domain.current_ip ? (
                                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                                  {domain.current_ip}
                                </code>
                              ) : (
                                <span className="text-muted-foreground text-sm">Not set</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {domain.record_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">{domain.ttl}s</span>
                            </TableCell>
                            <TableCell>
                              {domain.is_active ? (
                                <Badge variant="success">
                                  <CheckCircle className="mr-1 h-3 w-3" />
                                  Active
                                </Badge>
                              ) : (
                                <Badge variant="secondary">
                                  <XCircle className="mr-1 h-3 w-3" />
                                  Inactive
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm font-medium">{domain.update_count}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {domain.last_update_at_utc ? formatDate(domain.last_update_at_utc) : "Never"}
                              </span>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => loadDomainLogs(domain)}>
                                    <Eye className="h-4 w-4 mr-2" />
                                    View Logs
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleRegenerateApiKey(domain)}
                                    disabled={regeneratingKey}
                                  >
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Regenerate API Key
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => {
                                      setSelectedDomain(domain);
                                      setShowDnsDeleteDialog(true);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {dnsPagination.totalCount > 0 && (
                      <div className="mt-4 border-t pt-4">
                        <Pagination
                          page={dnsPagination.page}
                          totalPages={dnsPagination.totalPages}
                          totalCount={dnsPagination.totalCount}
                          pageSize={dnsPagination.pageSize}
                          pageSizeOptions={dnsPageSizeOptions}
                          startIndex={dnsStartIndex}
                          endIndex={dnsEndIndex}
                          canGoPrev={dnsCanGoPrev}
                          canGoNext={dnsCanGoNext}
                          onPageChange={dnsGoToPage}
                          onPageSizeChange={dnsSetPageSize}
                          onFirstPage={dnsFirstPage}
                          onLastPage={dnsLastPage}
                          onPrevPage={dnsPrevPage}
                          onNextPage={dnsNextPage}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* ================================================================ */}
        {/* Managed databases Tab (platform admin: org → Azure SQL server)    */}
        {/* ================================================================ */}
        {isAdminView && (
          <TabsContent value="managed-databases" className="space-y-6">
            <ManagedDatabasesTab token={token} />
          </TabsContent>
        )}
      </Tabs>

      {/* ================================================================ */}
      {/* Dialogs                                                          */}
      {/* ================================================================ */}

      {/* Create Peer Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add VPN Peer</DialogTitle>
            <DialogDescription>
              Create a new WireGuard VPN peer. A keypair will be generated
              automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="peer-name">Name</Label>
              <Input
                id="peer-name"
                placeholder="e.g. John, DevLaptop, Acme Corp"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && createName.trim()) {
                    handleCreatePeer();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="peer-type">Peer Type</Label>
              <Select
                value={createPeerType}
                onValueChange={(v) => {
                  const peerType = v as VpnPeerType;
                  setCreatePeerType(peerType);
                  // Customer peers are always split tunnel
                  if (peerType === "customer") {
                    setCreateTunnelMode("split");
                  }
                }}
              >
                <SelectTrigger id="peer-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal (bi-directional access)</SelectItem>
                  <SelectItem value="customer">Customer (Larasoft-initiated only)</SelectItem>
                </SelectContent>
              </Select>
              {createPeerType === "customer" && (
                <p className="text-xs text-amber-600">
                  Customer peers are isolated — they cannot initiate connections to Larasoft infrastructure or other customers.
                </p>
              )}
              {createPeerType === "customer" && <WireguardFirewallRequirements />}
            </div>
            <div className="space-y-2">
              <Label htmlFor="tunnel-mode">Tunnel Mode</Label>
              <Select
                value={createTunnelMode}
                onValueChange={(v) => setCreateTunnelMode(v as "full" | "split")}
                disabled={createPeerType === "customer"}
              >
                <SelectTrigger id="tunnel-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="split">
                    Split Tunnel (VPN traffic only)
                  </SelectItem>
                  <SelectItem value="full">
                    Full Tunnel (all traffic)
                  </SelectItem>
                </SelectContent>
              </Select>
              {createPeerType === "customer" && (
                <p className="text-xs text-muted-foreground">
                  Customer peers always use split tunnel.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePeer}
              disabled={!createName.trim() || creating}
            >
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Peer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Peer Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              WireGuard Configuration
              {selectedPeer && ` — ${selectedPeer.name}`}
            </DialogTitle>
            <DialogDescription>
              {selectedPeer?.peer_type === "customer"
                ? "Send this configuration to the customer for their WireGuard gateway setup."
                : "Scan the QR code with the WireGuard mobile app, or download the .conf file."}
            </DialogDescription>
          </DialogHeader>
          {configLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : peerConfig ? (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <QRCodeSVG value={peerConfig.config} size={220} level="M" />
              </div>

              {/* Config text */}
              <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto max-h-48 font-mono">
                {peerConfig.config}
              </pre>

              {selectedPeer?.peer_type === "customer" && <WireguardFirewallRequirements />}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleCopyConfig}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
                <Button className="flex-1" onClick={handleDownloadConfig}>
                  <FileDown className="mr-2 h-4 w-4" />
                  Download .conf
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>
                Config unavailable (peer may have been created before key
                storage was enabled)
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke Peer Confirmation */}
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke VPN Peer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke{" "}
              <strong>{selectedPeer?.name}</strong>? This will immediately
              disconnect them from the VPN and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokePeer}
              disabled={revoking}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {revoking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Revoke Peer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Domain Dialog */}
      <Dialog open={showDnsCreateDialog} onOpenChange={setShowDnsCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Dynamic DNS Domain</DialogTitle>
            <DialogDescription>
              Create a new domain for dynamic DNS updates. Configure your
              router or client to update this hostname.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="subdomain">Subdomain</Label>
              <Input
                id="subdomain"
                placeholder="e.g. home, office, router"
                value={createSubdomain}
                onChange={(e) => setCreateSubdomain(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="domain-name">Domain Name</Label>
              <Input
                id="domain-name"
                placeholder="e.g. example.com"
                value={createDomainName}
                onChange={(e) => setCreateDomainName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Full hostname will be: {createSubdomain || "subdomain"}.
                {createDomainName || "example.com"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="record-type">Record Type</Label>
                <Select
                  value={createRecordType}
                  onValueChange={setCreateRecordType}
                >
                  <SelectTrigger id="record-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A (IPv4)</SelectItem>
                    <SelectItem value="AAAA">AAAA (IPv6)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ttl">TTL (seconds)</Label>
                <Input
                  id="ttl"
                  type="number"
                  min="60"
                  max="86400"
                  value={createTtl}
                  onChange={(e) => setCreateTtl(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDnsCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateDomain}
              disabled={
                !createDomainName.trim() ||
                !createSubdomain.trim() ||
                creatingDomain
              }
            >
              {creatingDomain && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Domain Logs Dialog */}
      <Dialog open={showDnsLogsDialog} onOpenChange={setShowDnsLogsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update Logs — {selectedDomain?.hostname}</DialogTitle>
            <DialogDescription>
              Recent IP update attempts for this domain
            </DialogDescription>
          </DialogHeader>
          {logsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : domainLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No update logs yet</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Time
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Status
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Old IP
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      New IP
                    </SortableTableHead>
                    <SortableTableHead direction={null} onSort={() => {}} disabled>
                      Source
                    </SortableTableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {domainLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">
                        {formatDate(log.created_at_utc)}
                      </TableCell>
                      <TableCell>
                        {log.status === "good" ? (
                          <Badge variant="success">good</Badge>
                        ) : log.status === "nochg" ? (
                          <Badge variant="outline">nochg</Badge>
                        ) : (
                          <Badge variant="destructive">{log.status}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <code className="text-xs">
                          {log.old_ip || "—"}
                        </code>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs">{log.new_ip}</code>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs text-muted-foreground">
                          {log.source_ip}
                        </code>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Domain Confirmation */}
      <AlertDialog open={showDnsDeleteDialog} onOpenChange={setShowDnsDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Domain</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{selectedDomain?.hostname}</strong>? This will remove all
              update logs and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingDomain}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDomain}
              disabled={deletingDomain}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deletingDomain && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete Domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* API Key Dialog (shown after creation or regeneration) */}
      <Dialog open={showApiKeyDialog} onOpenChange={setShowApiKeyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>DynDNS API Key</DialogTitle>
            <DialogDescription>
              Save this API key now - it will not be shown again!
            </DialogDescription>
          </DialogHeader>
          {apiKeyData && (
            <div className="space-y-4 py-4">
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-900 dark:bg-yellow-950">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Important:</strong> This is the only time this API key will be displayed.
                  Copy it now and store it securely.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Hostname</Label>
                <div className="font-mono text-sm bg-muted px-3 py-2 rounded-md">
                  {apiKeyData.hostname}
                </div>
              </div>

              <div className="space-y-2">
                <Label>API Key (Password)</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={apiKeyData.api_key}
                    className="font-mono text-xs"
                  />
                  <Button variant="outline" onClick={handleCopyApiKey}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
                <p className="text-sm font-medium">Router Configuration</p>
                <p className="text-xs text-muted-foreground">
                  Configure your router with these settings:
                </p>
                <div className="grid gap-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Server URL:</span>
                    <code className="font-mono">https://api.larasoft.global/api/v1/gateway/dyndns/nic/update</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Username:</span>
                    <code className="font-mono">{apiKeyData.hostname}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Password:</span>
                    <code className="font-mono">[API Key above]</code>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowApiKeyDialog(false)}>
              I've Saved the Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
